import { getImpersonatedUserId, setActiveWorkspaceIdGetter } from '@doneverse/data/api'
import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { createCallSlice, type CallSlice } from './call'
import { createFeatureFlagsSlice, type FeatureFlagsSlice } from './featureFlags'
import { createUiSlice, type UiSlice } from './ui'
import { createWorkspaceSlice, type WorkspaceSlice } from './workspace'

export type DashboardStore = WorkspaceSlice & UiSlice & FeatureFlagsSlice & CallSlice

export type PersistMutator = [['zustand/persist', unknown]]

/**
 * Single dashboard-wide store. Persisted slices share one localStorage key so
 * we don't fragment storage across feature areas. Slice files own their state
 * shape and actions; this file only composes them and configures persistence.
 */
export const useDashboardStore = create<DashboardStore>()(
  persist(
    (...a) => ({
      ...createWorkspaceSlice(...a),
      ...createUiSlice(...a),
      ...createFeatureFlagsSlice(...a),
      ...createCallSlice(...a),
    }),
    {
      name: 'doneverse.dashboard',
      partialize: (state) => ({
        workspaceIdsByUserId: state.workspaceIdsByUserId,
        taskScope: state.taskScope,
        devFlagOverrides: state.devFlagOverrides,
        notificationSoundEnabled: state.notificationSoundEnabled,
        notificationSoundId: state.notificationSoundId,
      }),
    },
  ),
)

// Wire the API client to read the current workspace id directly from the
// store. Done at module load so the very first request after a page reload
// carries the `x-doneverse-workspace-id` header — no useEffect, no ref.
//
// The stored selection is per-user, so we resolve it against the effective
// user (the impersonated user, if any, otherwise the signed-in one). This
// keeps a workspace persisted for one user from leaking into another's
// requests after a switch or impersonation.
setActiveWorkspaceIdGetter(() => {
  const { currentUserId, workspaceIdsByUserId } = useDashboardStore.getState()
  const userId = getImpersonatedUserId() ?? currentUserId
  return userId ? (workspaceIdsByUserId[userId] ?? null) : null
})

export type { CallSlice, IncomingCall } from './call'
export { myRingState } from './call'
export type { ActiveCallSession, CallReleaseReason } from './call'
export type { WorkspaceSlice } from './workspace'
export type { UiSlice, TaskScope } from './ui'
export type { FeatureFlagsSlice } from './featureFlags'
export { EMPTY_FLAGS } from './featureFlags'
